import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
  BellOff,
  Clock,
  Inbox,
  Mail,
  MessageCircle,
  Pencil,
  Plane,
  Power,
  PowerOff,
  ShieldCheck,
  Smartphone,
  Users,
} from "lucide-react";

import { PessoaDTO } from "@/types/pessoa";
import { SquadDTO } from "@/types/squad.type";
import { Role } from "@/services/api/RoleService";
import {
  NotificacaoCanalMap,
  PessoaFiltrosCriteria,
  PessoaFiltrosOptions,
  RoleLabelsMap,
} from "@workflow/types";

import { Column, DataTable } from "@/components/table/DataTable";
import { useServerDataTable } from "@/hooks/data-table/use-server-data-table";
import { pessoasQueries, pessoasKeys } from "@/services/api/queries/pessoas.queries";
import { pessoaService } from "@/services/api/PessoaService";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ActionMenu, ActionMenuItem } from "@/components/shared/ActionMenu";
import { Person } from "@/components/shared/person";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PermissaoEnum } from "@workflow/types";
import { cn } from "@/lib/utils";

// --- Células reaproveitáveis ---
const roleLabel = (role: Role) => RoleLabelsMap[role.nome] || role.nome;

/** Estado vazio padrão das colunas: ícone tênue + rótulo em itálico. */
const EmptyCell = ({ icon: Icon, label }: { icon: typeof Users; label: string }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] italic text-muted-foreground/70">
    <Icon className="h-3 w-3 opacity-50" />
    {label}
  </span>
);

/** Chip de perfil: pílula com ícone de escudo e o rótulo do cargo. */
const RoleChip = ({ label }: { label: string }) => (
  <span
    className="inline-flex max-w-[160px] items-center gap-1.5 rounded-md border border-primary/15 bg-primary/[0.07] py-1 pl-1.5 pr-2 text-[11px] font-medium text-primary/90 transition-colors hover:bg-primary/10"
    title={label}
  >
    <ShieldCheck className="h-3 w-3 shrink-0 opacity-70" />
    <span className="truncate">{label}</span>
  </span>
);

/** Botão "+N" com tooltip listando os itens excedentes. */
const OverflowChip = ({ items, icon: Icon }: { items: string[]; icon: typeof Users }) => (
  <Tooltip>
    <TooltipTrigger
      type="button"
      className="inline-flex h-[26px] items-center rounded-md border border-border/60 bg-muted px-2 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      +{items.length}
    </TooltipTrigger>
    <TooltipContent align="start" className="max-w-[240px] p-2">
      <div className="flex flex-col gap-1">
        {items.map((item) => (
          <div key={item} className="flex items-center gap-1.5 text-xs">
            <Icon className="h-3 w-3 shrink-0 opacity-60" />
            <span className="truncate">{item}</span>
          </div>
        ))}
      </div>
    </TooltipContent>
  </Tooltip>
);

const UserRolesTags = ({ roles }: { roles?: Role[] }) => {
  if (!roles || roles.length === 0) {
    return <EmptyCell icon={ShieldCheck} label="Nenhum perfil" />;
  }

  const visible = roles.slice(0, 2);
  const overflow = roles.slice(2);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((role) => (
        <RoleChip key={role.id} label={roleLabel(role)} />
      ))}
      {overflow.length > 0 && <OverflowChip items={overflow.map(roleLabel)} icon={ShieldCheck} />}
    </div>
  );
};

const UserSquadTags = ({ squads }: { squads?: SquadDTO[] }) => {
  if (!squads || squads.length === 0) {
    return <EmptyCell icon={Users} label="Nenhum squad" />;
  }

  const visible = squads.slice(0, 2);
  const overflow = squads.slice(2);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((squad) => (
        <span
          key={squad.id}
          className="inline-flex max-w-[140px] items-center gap-1.5 rounded-md border border-sky-500/20 bg-sky-500/[0.07] py-1 pl-1.5 pr-2 text-[11px] font-medium text-sky-700 transition-colors hover:bg-sky-500/10 dark:text-sky-300"
          title={squad.nome}
        >
          <Users className="h-3 w-3 shrink-0 opacity-70" />
          <span className="truncate">{squad.nome}</span>
        </span>
      ))}
      {overflow.length > 0 && <OverflowChip items={overflow.map((s) => s.nome)} icon={Users} />}
    </div>
  );
};

/** Ícone + cor por canal de notificação. */
const canalConfig: Record<string, { icon: typeof Mail; className: string }> = {
  EMAIL: {
    icon: Mail,
    className: "border-blue-500/20 bg-blue-500/[0.07] text-blue-700 dark:text-blue-300",
  },
  WHATSAPP: {
    icon: MessageCircle,
    className: "border-emerald-500/20 bg-emerald-500/[0.07] text-emerald-700 dark:text-emerald-300",
  },
  SMS: {
    icon: Smartphone,
    className: "border-violet-500/20 bg-violet-500/[0.07] text-violet-700 dark:text-violet-300",
  },
  INBOX: {
    icon: Inbox,
    className: "border-amber-500/20 bg-amber-500/[0.07] text-amber-700 dark:text-amber-300",
  },
};

const NotificacaoTags = ({ canais }: { canais?: string[] }) => {
  if (!canais || canais.length === 0) {
    return <EmptyCell icon={BellOff} label="Nenhuma" />;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {canais.map((canal) => {
        const config = canalConfig[canal];
        const Icon = config?.icon;
        return (
          <span
            key={canal}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border py-1 pl-1.5 pr-2 text-[11px] font-medium",
              config?.className ?? "border-border/60 bg-muted text-muted-foreground",
            )}
          >
            {Icon && <Icon className="h-3 w-3 shrink-0 opacity-70" />}
            {NotificacaoCanalMap[canal as keyof typeof NotificacaoCanalMap] || canal}
          </span>
        );
      })}
    </div>
  );
};

/** Status do usuário: pílula Ativo/Inativo, com marcador "Em férias" inline. */
const StatusCell = ({ ativo, ausente }: { ativo?: boolean; ausente?: boolean }) => (
  <div className="flex items-center gap-1.5">
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border py-1 pl-1.5 pr-2 text-[11px] font-medium",
        ativo
          ? "border-emerald-500/20 bg-emerald-500/[0.07] text-emerald-700 dark:text-emerald-300"
          : "border-border/60 bg-muted text-muted-foreground",
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          ativo ? "bg-emerald-500" : "bg-muted-foreground/50",
        )}
      />
      {ativo ? "Ativo" : "Inativo"}
    </span>
    {ausente && (
      <Tooltip>
        <TooltipTrigger>
          <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md border border-orange-500/20 bg-orange-500/[0.07] text-orange-600 dark:text-orange-400">
            <Plane className="h-3.5 w-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent>Em férias</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const UltimoLogin = ({ ultimoLogin }: { ultimoLogin?: string }) => {
  if (!ultimoLogin) {
    return <EmptyCell icon={Clock} label="Nunca acessou" />;
  }

  const data = new Date(ultimoLogin);
  const umMesAtras = new Date();
  umMesAtras.setMonth(umMesAtras.getMonth() - 1);
  const inativo = data < umMesAtras;

  const relativo = formatDistanceToNow(data, { locale: ptBR, addSuffix: true });
  const absoluto = format(data, "dd/MM/yyyy 'às' HH:mm");

  return (
    <Tooltip>
      <TooltipTrigger>
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn(
              "h-1.5 w-1.5 shrink-0 rounded-full",
              inativo ? "bg-red-500" : "bg-emerald-500",
            )}
          />
          <span
            className={cn(
              "whitespace-nowrap text-xs first-letter:uppercase",
              inativo ? "font-medium text-red-600 dark:text-red-400" : "text-foreground",
            )}
          >
            {relativo}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {inativo ? "Sem acessar há mais de 1 mês · " : ""}
        {absoluto}
      </TooltipContent>
    </Tooltip>
  );
};

interface UserListTableProps {
  /** Abre o diálogo de edição de perfil/squads para a pessoa selecionada. */
  onEditUser: (user: PessoaDTO) => void;
  /** Permite a ação de edição na linha (gerenciada pelo pai via usePermissions). */
  canEdit: boolean;
}

export function UserListTable({ onEditUser, canEdit }: UserListTableProps) {
  const { tableProps, isError, invalidate } = useServerDataTable<
    PessoaDTO,
    PessoaFiltrosOptions,
    PessoaFiltrosCriteria
  >({
    queryOptions: pessoasQueries.list,
    filters: pessoasQueries.filtros,
    invalidateKey: pessoasKeys.list,
  });

  const queryClient = useQueryClient();

  // Confirmação de inativar/reativar
  const [pessoaToToggle, setPessoaToToggle] = useState<PessoaDTO | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  const handleConfirmToggle = async () => {
    if (!pessoaToToggle) return;
    setIsToggling(true);
    try {
      if (pessoaToToggle.ativo) {
        await pessoaService.deletePessoa(pessoaToToggle.id);
        toast.success("Usuário inativado com sucesso.");
      } else {
        await pessoaService.ativarPessoa(pessoaToToggle.id);
        toast.success("Usuário reativado com sucesso.");
      }
      invalidate();
      queryClient.invalidateQueries({ queryKey: pessoasKeys.resumo });
      setPessoaToToggle(null);
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível alterar o status do usuário.");
    } finally {
      setIsToggling(false);
    }
  };

  const columns = useMemo<Column<PessoaDTO>[]>(
    () => [
      {
        type: "headless",
        key: "actions",
        title: "Ações",
        visible: canEdit,
        width: "min-w-[40px]",
        render: (user) => {
          if (!canEdit) return null;
          const actions: ActionMenuItem[] = [
            {
              label: "Editar perfil e squads",
              icon: <Pencil className="h-4 w-4" />,
              onClick: () => onEditUser(user),
              permissions: [PermissaoEnum.PESSOA_ADD_ROLE],
            },
            { label: "separador", separator: true },
            user.ativo
              ? {
                  label: "Inativar",
                  icon: <PowerOff className="h-4 w-4" />,
                  danger: true,
                  onClick: () => setPessoaToToggle(user),
                  permissions: [PermissaoEnum.PESSOA_DELETE],
                }
              : {
                  label: "Reativar",
                  icon: <Power className="h-4 w-4" />,
                  onClick: () => setPessoaToToggle(user),
                  permissions: [PermissaoEnum.PESSOA_CREATE],
                },
          ];
          return <ActionMenu items={actions} />;
        },
      },
      {
        key: "pessoa.id",
        title: "ID",
        sortable: true,
        width: "min-w-[80px]",
        render: (user) => (
          <span className="font-mono text-xs text-muted-foreground">{user.id}</span>
        ),
        raw: (user) => user.id.toString(),
      },
      {
        key: "pessoa.nome",
        title: "Usuário",
        sortable: true,
        width: "min-w-[250px]",
        render: (user) => (
          <Person.Root pessoa={user} asClickable className="flex items-center gap-3">
            <Person.Avatar className="h-8 w-8" />
            <div className="flex min-w-0 flex-col">
              <Person.Name className="truncate text-sm font-semibold text-foreground" />
              <Person.Email className="truncate text-[11px] text-muted-foreground" />
            </div>
          </Person.Root>
        ),
        raw: (user) => `${user.nome} ${user.email}`,
      },
      {
        key: "pessoa.ativo",
        title: "Status",
        sortable: true,
        width: "min-w-[120px]",
        render: (user) => <StatusCell ativo={user.ativo} ausente={user.ausente} />,
        raw: (user) => (user.ativo ? "Ativo" : "Inativo"),
      },
      {
        key: "squads",
        title: "Squads",
        width: "min-w-[160px]",
        render: (user) => (
          <UserSquadTags
            squads={user.squads?.slice().sort((a, b) => a.nome.localeCompare(b.nome))}
          />
        ),
        raw: (user) => user.squads?.map((s) => s.nome).join(", ") || "",
      },
      {
        key: "roles",
        title: "Perfis de Acesso",
        width: "min-w-[180px]",
        render: (user) => (
          <UserRolesTags roles={user.roles?.slice().sort((a, b) => a.nome.localeCompare(b.nome))} />
        ),
        raw: (user) => user.roles?.map((r) => RoleLabelsMap[r.nome] || r.nome).join(", ") || "",
      },
      {
        key: "notificacoes",
        title: "Notificações",
        width: "min-w-[160px]",
        render: (user) => <NotificacaoTags canais={user.notificacaoPreferencia?.canais} />,
        raw: (user) =>
          user.notificacaoPreferencia?.canais?.map((c) => NotificacaoCanalMap[c] || c).join(", ") ||
          "",
      },
      {
        key: "pessoa.ultimoLogin",
        title: "Último Login",
        sortable: true,
        width: "min-w-[160px]",
        render: (user) => <UltimoLogin ultimoLogin={user.ultimoLogin} />,
        raw: (user) => user.ultimoLogin || "",
      },
    ],
    [canEdit, onEditUser],
  );

  return (
    <>
      <DataTable
        {...tableProps}
        storageKey="pessoas-listagem-v1"
        columns={columns}
        defaultSortColumn="pessoa.id"
        defaultSortOrder="DESC"
        searchPlaceholder="Pesquisar por nome, email ou ID..."
        emptyMessage={
          isError
            ? "Não foi possível carregar os usuários. Tente recarregar."
            : "Nenhum usuário encontrado."
        }
        isError={isError}
        onRetry={invalidate}
        exportFilename="usuarios"
        rowClassName={(user) => (user.ativo ? "" : "opacity-50 bg-muted/10")}
      />

      <AlertDialog
        open={!!pessoaToToggle}
        onOpenChange={(open) => !open && setPessoaToToggle(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirmar {pessoaToToggle?.ativo ? "Inativação" : "Reativação"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Você tem certeza que deseja {pessoaToToggle?.ativo ? "inativar" : "reativar"} o
              usuário <span className="font-bold text-foreground">{pessoaToToggle?.nome}</span>?
              {pessoaToToggle?.ativo && " Ele perderá o acesso ao sistema."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isToggling}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmToggle();
              }}
              disabled={isToggling}
              className={
                pessoaToToggle?.ativo
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-emerald-600 text-white hover:bg-emerald-700"
              }
            >
              {isToggling ? "Aguarde..." : "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
